<template>
  <div v-cloak data-test-component="timeout">
    <transition name="modal">
      <v-modal :is-open="showModal" @close="closeModal">
        <template #title>
          <h3>
            {{ modalData.title }}
          </h3>
        </template>
        <template #content>
          <div v-html="modalData.content" />
        </template>
        <template #buttons>
          <div>
            <div class="row">
              <div
                v-for="(button, index) in modalData.buttons"
                :key="index"
                class="medium-6 columns"
              >
                <button class="button large" @click="clickAction(button)">
                  {{ button.ButtonText }}
                </button>
              </div>
            </div>
          </div>
        </template>
      </v-modal>
    </transition>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
import Utility from '@/common/UtilityFunctions.js';
import BaseMixin from '../BaseMixin.vue';
import { Countdown } from '@/common/Util/Countdown';

export default {
  name: 'Timeout',
  mixins: [BaseMixin],
  props: {
    model: {
      type: Object,
      required: true,
      default() {
        return null;
      }
    },
    sessionDurationSeconds: {
      type: Number,
      default() {
        return 20;
      }
    },
    secondsRemainingBeforeWarning: {
      type: Number,
      default() {
        return 10;
      }
    },
    debugMode: {
      type: Boolean,
      default() {
        return true;
      }
    }
  },
  data() {
    return {
      stopPullingSessionTimeout: false,
      timeoutInterval: 1000,
      timeBeforeWarning: this.secondsRemainingBeforeWarning * 1000,
      showModal: false,
      isLoggedIn: false,
      modalData: {
        title: null,
        content: null,
        buttons: null
      },
      timer: null,
      countdownSeconds: null,
      timeOutCookie: null
    };
  },
  computed: {
    loggedOut() {
      return Utility.getQueryVariable('reason') === 'timeout';
    },
    countdownAsSeconds() {
      return this.countdownSeconds.countdown;
    },
    cookie() {
      return document.cookie;
    }
  },
  watch: {
    cookie(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.resetCountdown();
      }
    }
  },
  mounted() {
    if (this.loggedOut) {
      this.modalData.title = this.model.loggedOutNotification.Title;
      this.modalData.content = this.model.loggedOutNotification.Content;
      this.modalData.buttons = this.model.loggedOutNotification.Buttons;
      this.showModal = true;
    }

    this.isLoggedIn = this.getLoginState();
    this.log(['mounted()', 'isLoggedIn', this.isLoggedIn]);
    this.startCountdownCheck();
  },
  methods: {
    startCountdownCheck() {
      this.log(['startCountdownCheck()', 'isLoggedIn', this.isLoggedIn]);

      if (!this.isLoggedIn) {
        return;
      }

      if (this.timeOutCookie === undefined) {
        return;
      }

      this.resetCountdown();
    },
    resetCountdown() {
      this.setTimeoutCookie();
      this.setCountdown();
    },
    setTimeoutCookie() {
      this.timeOutCookie = this.readCookie('timeoutcookie');
    },
    setCountdown() {
      this.countdownSeconds = new Countdown(
        this.sessionDurationSeconds,
        this.timeoutInterval,
        this.checkTimeout
      );
    },
    checkTimeout() {
      const message = [];
      const time = this.countdownAsSeconds - this.timeBeforeWarning;
      message.push(`session ending in ${this.countdownSeconds.asSeconds()} seconds`);

      if (time >= 0) {
        message.push(`- showing WARNING in: ${time} seconds`);
      }
      this.log(message);

      // show warning
      if (time <= 0) {
        this.sessionWarning();
      }

      // show logout modal
      if (this.countdownAsSeconds <= 0) {
        this.sessionLogout();
      }
    },
    sessionWarning() {
      this.setModalData(
        this.model.inActiveWarning.Title,
        this.model.inActiveWarning.Content.replace('{countdown}', this.countdownSeconds.asString()),
        this.model.inActiveWarning.Buttons
      );
      this.showModal = true;
    },
    sessionLogout() {
      this.log([this.countdownAsSeconds, 'seconds since last http request, logging out']);
      Utility.logError(
        `
          TIMEOUT COMPONENT: Session expired after ${this.sessionDurationSeconds}.
          local machine timer(browser utc):   ${moment().utc().format()} 
          backend-time(cookie):               ${this.timeOutCookie}
          sessionDurationSeconds:             ${this.sessionDurationSeconds} seconds
        `,
        this,
        () => {
          this.countdownSeconds.stopCountdown();
          window.location = Utility.constants().API.timeout_logout;
        }
      );
    },
    extendSession() {
      this.log(['extending session ...']);
      axios.get(Utility.constants().API.keep_alive_session).then(() => {
        this.log(['session successfully extended']);
        this.showModal = false;
        this.resetCountdown();
      });
    },
    readCookie(name) {
      name += '=';
      for (var ca = document.cookie.split(/;\s*/), i = ca.length - 1; i >= 0; i--) {
        if (!ca[i].indexOf(name)) {
          this.log(['readCookie()', 'cookie name and value', ca[i]]);
          this.log(['readCookie()', 'cookie value', ca[i].replace(name, '')]);
          return ca[i].replace(name, '');
        }
      }
    },
    getLoginState() {
      const isLoggedInCookie = this.readCookie('isloggedincookie');
      this.log(['getLoginState()', 'isLoggedInCookie', isLoggedInCookie]);
      return isLoggedInCookie !== undefined && isLoggedInCookie.toLowerCase() === 'true';
    },
    setModalData(title, content, buttons) {
      this.modalData.title = title;
      this.modalData.content = content;
      this.modalData.buttons = buttons;
    },
    clickAction(button) {
      if (button.Action === null && button.Url !== null) {
        return (window.location = button.Url);
      }

      if (button.Action === null) {
        return null;
      }

      switch (button.Action) {
        case 'keepAlive':
          this.extendSession();
          break;
      }
    },
    closeModal() {
      if (this.loggedOut) {
        return (this.showModal = false);
      }

      return this.extendSession();
    }
  }
};
</script>
